/**
 * Permission Engine Utility
 * 
 * Framework-agnostic permission evaluation engine that works with:
 * - Existing WordPress / WooCommerce capabilities
 * - Custom business_* capabilities
 * 
 * Supports AND / OR logic for complex permission rules.
 */

export interface PermissionRule {
  /**
   * Required roles (OR logic - user needs at least one)
   */
  roles?: string[];
  
  /**
   * Required capabilities (AND logic - user needs all)
   */
  capabilities?: string[];
  
  /**
   * Required capabilities with OR logic (user needs at least one)
   */
  anyCapability?: string[];
}

export interface UserPermissions {
  roles: Set<string>;
  capabilities: Set<string>;
}

/**
 * Evaluate a permission rule against user permissions
 * 
 * @param rule - The permission rule to evaluate
 * @param userPermissions - User's roles and capabilities
 * @returns true if user has permission, false otherwise
 */
export function evaluatePermission(
  rule: PermissionRule,
  userPermissions: UserPermissions
): boolean {
  // If no rule specified, allow access
  if (!rule || (!rule.roles && !rule.capabilities && !rule.anyCapability)) {
    return true;
  }

  // Check roles (OR logic - user needs at least one)
  if (rule.roles && rule.roles.length > 0) {
    const hasRole = rule.roles.some(role => userPermissions.roles.has(role));
    if (!hasRole) {
      return false;
    }
  }

  // Check capabilities (AND logic - user needs all)
  if (rule.capabilities && rule.capabilities.length > 0) {
    const hasAllCapabilities = rule.capabilities.every(cap =>
      userPermissions.capabilities.has(cap)
    );
    if (!hasAllCapabilities) {
      return false;
    }
  }

  // Check anyCapability (OR logic - user needs at least one)
  if (rule.anyCapability && rule.anyCapability.length > 0) {
    const hasAnyCapability = rule.anyCapability.some(cap =>
      userPermissions.capabilities.has(cap)
    );
    if (!hasAnyCapability) {
      return false;
    }
  }

  return true;
}

/**
 * Check if user has a specific role
 */
export function hasRole(role: string, userPermissions: UserPermissions): boolean {
  return userPermissions.roles.has(role);
}

/**
 * Check if user has any of the specified roles
 */
export function hasAnyRole(
  roles: string[],
  userPermissions: UserPermissions
): boolean {
  return roles.some(role => userPermissions.roles.has(role));
}

/**
 * Check if user has a specific capability
 */
export function hasCapability(
  capability: string,
  userPermissions: UserPermissions
): boolean {
  return userPermissions.capabilities.has(capability);
}

/**
 * Check if user has any of the specified capabilities
 */
export function hasAnyCapability(
  capabilities: string[],
  userPermissions: UserPermissions
): boolean {
  return capabilities.some(cap => userPermissions.capabilities.has(cap));
}

/**
 * Check if user has all of the specified capabilities
 */
export function hasAllCapabilities(
  capabilities: string[],
  userPermissions: UserPermissions
): boolean {
  return capabilities.every(cap => userPermissions.capabilities.has(cap));
}

/**
 * Check if user is super admin
 * (has super_admin_business role OR Administrator role)
 */
export function isSuperAdmin(userPermissions: UserPermissions): boolean {
  return (
    userPermissions.roles.has('super_admin_business') ||
    userPermissions.roles.has('administrator')
  );
}

/**
 * Create UserPermissions object from arrays
 */
export function createUserPermissions(
  roles: string[],
  capabilities: string[]
): UserPermissions {
  return {
    roles: new Set(roles),
    capabilities: new Set(capabilities),
  };
}
